import {
  Alert,
  alpha,
  Box,
  ButtonBase,
  Fade,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import { groupBy } from 'lodash';
import { ComponentProps, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { InfoCircleFilledIcon } from '@/components/icons/InfoCircleFilledIcon';
import { Annotations } from '@/modules/pdf/PDFViewer/Annotations';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

import { useFileParagraphs } from './FileParagraphsProvider';
import { CitationFileParagraphsQuery } from './graphql/FileParagraphPicker.generated';
import { SEARCH_HIGHLIGHT_COLORS } from './searchHighlightPalettes';

// A short message, because it only confirms why a click did nothing.
const ALREADY_CITED_MESSAGE_MS = 1500;
const MESSAGE_FADE_MS = { enter: 180, exit: 160 };

export type CitationParagraph =
  CitationFileParagraphsQuery['getFileParagraphs'][number];

export interface ParagraphPickerOptions {
  action?: 'add' | 'remove';
  excludedParagraphIds: string[];
  eligibleParagraphIds?: string[];
  /** Paragraphs of the reference that the question currently shows. */
  activeParagraphIds?: string[];
  onCancel?: () => void;
  onSelect: (
    paragraph: CitationParagraph,
    document: { id: string; name: string }
  ) => void;
}

interface FileParagraphPickerProps extends ComponentProps<typeof Annotations> {
  documentId: string;
  documentName: string;
  picker: ParagraphPickerOptions;
}

/** Selects extracted paragraphs on the displayed PDF without scrolling or zooming. */
export function FileParagraphPicker({
  documentId,
  documentName,
  picker,
  pageViewports,
  pageOffsets,
  filePages,
  visiblePageNumbers,
}: FileParagraphPickerProps) {
  const { data, loading, error, refetch } = useFileParagraphs();
  // The message stays anchored while it fades out, so `open` is separate.
  const [alreadyCited, setAlreadyCited] = useState<{
    anchor: HTMLElement;
    open: boolean;
    shownAt: number;
  }>();
  useEffect(() => {
    if (!alreadyCited?.open) return;
    const timer = setTimeout(
      () =>
        setAlreadyCited((current) => current && { ...current, open: false }),
      ALREADY_CITED_MESSAGE_MS
    );
    return () => clearTimeout(timer);
  }, [alreadyCited?.open, alreadyCited?.shownAt]);
  const [pending, setPending] = useState<{
    paragraph: CitationParagraph;
    anchor: HTMLElement;
    pageNumber: number;
  }>();
  // Index once per query result. Scrolling and hover only visit nearby pages.
  const paragraphsByPage = useMemo(() => {
    const pages = new Map<
      number,
      { paragraph: CitationParagraph; index: number }[]
    >();
    for (const paragraph of data?.getFileParagraphs ?? []) {
      if (
        picker.action === 'remove' &&
        !picker.eligibleParagraphIds?.includes(paragraph.id)
      )
        continue;
      if (!paragraph.content.trim()) continue;
      paragraph.boundingRegions.forEach((region, index) => {
        if (region.Polygon.length < 3) return;
        const entries = pages.get(region.PageNumber) ?? [];
        entries.push({ paragraph, index });
        pages.set(region.PageNumber, entries);
      });
    }
    return pages;
  }, [data, picker.action, picker.eligibleParagraphIds]);
  const bufferedPages = new Set<number>();
  // Include one page on each side so targets are ready before they enter view.
  for (const page of visiblePageNumbers ?? paragraphsByPage.keys()) {
    for (const nearbyPage of [page - 1, page, page + 1]) {
      if (nearbyPage >= 1 && nearbyPage <= pageOffsets.length)
        bufferedPages.add(nearbyPage);
    }
  }
  const pendingIsVisible = !!pending && bufferedPages.has(pending.pageNumber);
  useEffect(() => {
    // The confirmation cannot outlive the target that anchors it.
    if (pending && !pendingIsVisible) setPending(undefined);
  }, [pending, pendingIsVisible]);
  const visibleParagraphs = Array.from(bufferedPages)
    .sort((a, b) => a - b)
    .flatMap((page) => paragraphsByPage.get(page) ?? []);
  const regions = visibleParagraphs.flatMap(({ paragraph, index }) => {
    // The index comes from this paragraph's boundingRegions above.
    const region = paragraph.boundingRegions[index]!;
    const pageIndex = region.PageNumber - 1;
    const viewport = pageViewports[pageIndex];
    const offset = pageOffsets[pageIndex];
    if (
      !viewport ||
      offset === undefined ||
      region.Polygon.length < 3 ||
      !paragraph.content.trim()
    ) {
      return [];
    }
    // Extraction coordinates use page inches, as in the existing annotation renderer.
    const scaleX = viewport.width / (filePages[pageIndex]?.widthInches ?? 8.5);
    const scaleY = viewport.height / (filePages[pageIndex]?.heightInches ?? 11);
    const xs = region.Polygon.map((point) => point.x * scaleX);
    const ys = region.Polygon.map((point) => point.y * scaleY);
    return [
      {
        paragraph,
        index,
        pageNumber: region.PageNumber,
        left: Math.min(...xs),
        top: Math.min(...ys) + offset,
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys),
      },
    ];
  });

  if (loading || error || paragraphsByPage.size === 0) {
    let message = 'No selectable paragraphs are available for this file.';
    if (loading) message = 'Loading document paragraphs…';
    if (error) message = 'Paragraphs could not load.';
    return (
      <Alert
        severity={error ? 'error' : 'info'}
        sx={{ position: 'absolute', top: 64, zIndex: zIndexes.MENU }}
        action={
          error && (
            <Button
              size="xs"
              variant="secondary"
              onClick={() => {
                void refetch();
              }}
            >
              Try again
            </Button>
          )
        }
      >
        {message}
      </Alert>
    );
  }

  const highlightColor =
    picker.action === 'remove'
      ? COLORS.FUNCTIONAL.ERROR[500]
      : SEARCH_HIGHLIGHT_COLORS.citationStrong;
  return (
    <Box
      sx={{
        display: 'contents',
        '& [data-citation-group]': { display: 'contents' },
        '& [data-citation-group] > button': {
          position: 'absolute',
          zIndex: zIndexes.HIGHLIGHT,
          borderRadius: 0.5,
          cursor: 'pointer',
          bgcolor: alpha(highlightColor, 0.07),
        },
        // Paragraphs that the question already cites use the citation's
        // dashed border and a darker fill, so they read as already chosen.
        '& [data-citation-group][data-excluded="true"] > button': {
          cursor: 'not-allowed',
          outline: `2px dashed ${SEARCH_HIGHLIGHT_COLORS.citationBorder}`,
          outlineOffset: '-2px',
          bgcolor: alpha(SEARCH_HIGHLIGHT_COLORS.citationStrong, 0.18),
        },
        // The reference that the question's arrows select. Removal shows
        // it as the citation it is, with a dashed border and darker fill.
        '& [data-citation-group][data-active="true"] > button': {
          outline: `2px dashed ${highlightColor}`,
          outlineOffset: '-2px',
          bgcolor: alpha(highlightColor, 0.18),
        },
        // A paragraph can have several regions. CSS highlights them together
        // without a React update for each pointer or keyboard focus change.
        '& [data-citation-group]:not([data-excluded="true"]):is(:has(button:hover), :has(button:focus-visible), [data-selected="true"]) > button':
          {
            outline: `2px solid ${highlightColor}`,
            // Translucent, so that the paragraph text stays readable.
            bgcolor: alpha(
              highlightColor,
              picker.action === 'remove' ? 0.16 : 0.12
            ),
          },
      }}
    >
      {Object.values(groupBy(regions, (region) => region.paragraph.id)).map(
        (paragraphRegions) => {
          // Each group contains the regions of one paragraph from the page index.
          const paragraph = paragraphRegions[0]!.paragraph;
          const excluded = picker.excludedParagraphIds.includes(paragraph.id);
          const actionTitle =
            picker.action === 'remove'
              ? 'Remove this paragraph reference'
              : 'Add this paragraph as a citation';
          return (
            <span
              key={paragraph.id}
              data-citation-group
              data-excluded={excluded}
              data-active={
                picker.action === 'remove' &&
                !!picker.activeParagraphIds?.includes(paragraph.id)
              }
              data-selected={pending?.paragraph.id === paragraph.id}
            >
              {paragraphRegions.map(
                ({ index, pageNumber, paragraph: _paragraph, ...geometry }) => (
                  <ButtonBase
                    key={`${paragraph.id}:${index}`}
                    data-testid="pdf-citation-paragraph"
                    data-paragraph-id={paragraph.id}
                    aria-label={`Paragraph ${paragraph.impliedParagraphNumber}, page ${pageNumber}${excluded ? ', Already cited' : ''}`}
                    aria-disabled={excluded}
                    title={
                      excluded ? 'Already cited for this question' : actionTitle
                    }
                    onClick={(event) => {
                      if (excluded) {
                        setPending(undefined);
                        setAlreadyCited({
                          anchor: event.currentTarget,
                          open: true,
                          shownAt: event.timeStamp,
                        });
                        return;
                      }
                      setPending({
                        paragraph,
                        anchor: event.currentTarget,
                        pageNumber,
                      });
                    }}
                    style={geometry}
                  />
                )
              )}
            </span>
          );
        }
      )}
      <Popper
        open={pendingIsVisible}
        anchorEl={pending?.anchor}
        placement="bottom"
        modifiers={[
          {
            name: 'offset',
            options: { offset: [0, 8] },
          },
        ]}
        sx={{ zIndex: zIndexes.POPOVER }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 0.5,
            borderColor: COLORS.GRAY[200],
            boxShadow: (theme) => theme.palette.shadows.sm,
          }}
        >
          <Stack direction="row" gap={0.5}>
            <Button
              size="xs"
              variant={
                picker.action === 'remove' ? 'destructive-primary' : 'primary'
              }
              onClick={() => {
                if (!pending) return;
                picker.onSelect(pending.paragraph, {
                  id: documentId,
                  name: documentName,
                });
                setPending(undefined);
              }}
            >
              {picker.action === 'remove'
                ? 'Remove reference'
                : 'Add reference'}
            </Button>
            <Button
              size="xs"
              variant="transparent"
              onClick={() => {
                setPending(undefined);
                picker.onCancel?.();
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Paper>
      </Popper>
      <Popper
        open={!!alreadyCited?.open}
        anchorEl={alreadyCited?.anchor}
        placement="top"
        transition
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        sx={{ zIndex: zIndexes.POPOVER, pointerEvents: 'none' }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={MESSAGE_FADE_MS}>
            <Stack
              role="status"
              direction="row"
              alignItems="center"
              gap={1}
              sx={(theme) => ({
                px: 1.5,
                py: 0.75,
                borderRadius: '8px',
                bgcolor: COLORS.PRIMITIVES.WHITE,
                border: `1px solid ${COLORS.GRAY[200]}`,
                boxShadow: theme.palette.shadows.md,
              })}
            >
              <InfoCircleFilledIcon
                size={16}
                color={SEARCH_HIGHLIGHT_COLORS.citationStrong}
              />
              <Typography variant="body-s" sx={{ color: COLORS.TEXT.PRIMARY }}>
                Citation already added
              </Typography>
            </Stack>
          </Fade>
        )}
      </Popper>
    </Box>
  );
}
